import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Row as AntRow, Col } from "antd";
import styled from "styled-components";

import { WHITE, BLACK } from "../../constant/colors";
import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import PhysicianIcon from "../../assets/svg/PhysicianIcon";
import { selectProvidersAllDoctors, selectUsers, selectUser, selectEnvironment } from "../../store/selector";
import ACTIONS from "../../store/action";
import {
  StatusTag,
  Section,
  SectionTitle,
  BreadcrumbSection,
  BreadcrumbLeft,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  MetaText,
  CreatedText,
  UpdatedText,
  StyledPrimaryButton
} from "./shared/components";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import {
  handleSettingsClick as sharedHandleSettingsClick,
  handlePhysiciansClick as sharedHandlePhysiciansClick
} from "./shared/handlers";
import { getUserName } from "./shared/utils";
import { dateTimeFormatter } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/formaters";

const { Text } = Typography;

const PhysicianDetailContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  overflow-y: auto;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
  margin-bottom: 16px;
`;

const Row = styled(AntRow)`
  padding-right: 155px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
`;

const FieldLabel = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${BLACK};
`;

const FieldValue = styled(Text)`
  font-size: 16px;
  color: ${BLACK};
`;

const PhysicianDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const physicians = useSelector(selectProvidersAllDoctors) || [];
  const users = useSelector(selectUsers) || {};
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const [isLoading, setIsLoading] = React.useState(true);

  const physician = physicians.find((p) => p.id === parseInt(id));

  const handleSettingsClick = () => sharedHandleSettingsClick({ dispatch, user, account, history });

  const handlePhysiciansClick = () => sharedHandlePhysiciansClick({ dispatch, user, account, history });

  // Fetch physicians on mount if store is empty
  useEffect(() => {
    if (physicians.length === 0) {
      dispatch(ACTIONS.providersAction("doctor"));
    }
  }, [dispatch, physicians.length]);

  // Check if physician exists after data is loaded
  useEffect(() => {
    if (physicians.length > 0) {
      if (!physician) {
        // Physician data loaded but physician not found - redirect
        history.push("/physicians-settings");
      } else {
        // Data loaded and physician found
        setIsLoading(false);
      }
    }
  }, [physician, physicians.length, history]);

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (!physician) {
    return null;
  }

  const handleEdit = () => {
    try {
      trackPendoEvent(dispatch, "Physician_Edit", {
        username: user?.email,
        account,
        physicianId: physician.id
      });
    } catch (error) {
      console.error("Error dispatching physician edit Pendo event:", error);
    }
    history.push(`/physicians-settings/${physician.id}/edit`);
  };

  return (
    <PhysicianDetailContainer>
      <HeaderSection>
        <BreadcrumbSection>
          <BreadcrumbLeft>
            <BreadcrumbItem id="Physician_Breadcrumbs_Settings" onClick={handleSettingsClick}>
              <SettingsIcon />
              {t("physician_management.breadcrumb.settings")}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem id="Physician_Breadcrumbs_Physicians" onClick={handlePhysiciansClick}>
              <PhysicianIcon />
              {t("physician_management.breadcrumb.physicians")}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbCurrent>
              {physician.name || t("physician_management.breadcrumb.physician_detail")}
            </BreadcrumbCurrent>
            <StatusTag className={physician.isActive ? "active-tag" : "inactive-tag"}>
              {physician.isActive
                ? t("physician_management.list.active_status")
                : t("physician_management.list.inactive_status")}
            </StatusTag>
          </BreadcrumbLeft>
          <StyledPrimaryButton id="Physician_Edit" type="primary" onClick={handleEdit}>
            {t("physician_management.detail.button_edit")}
          </StyledPrimaryButton>
        </BreadcrumbSection>
        <MetaText>
          <CreatedText>
            {t("physician_management.detail.created_by", {
              user: getUserName(users, physician.createdBy, t("physician_management.detail.default_user")),
              date: dateTimeFormatter(physician.createdAt)
            })}
          </CreatedText>
          {physician.updatedBy && physician.userUpdatedAt && (
            <UpdatedText>
              {t("physician_management.detail.updated_by", {
                user: getUserName(users, physician.updatedBy, t("physician_management.detail.default_user")),
                date: dateTimeFormatter(physician.userUpdatedAt)
              })}
            </UpdatedText>
          )}
        </MetaText>
      </HeaderSection>

      <Section>
        <SectionTitle>{t("physician_management.form.section_main")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_npi")}</FieldLabel>
              <FieldValue>{physician.npi || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_name")}</FieldLabel>
              <FieldValue>{physician.name || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_title")}</FieldLabel>
              <FieldValue>{physician.title || "-"}</FieldValue>
            </Field>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_practice")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_address")}</FieldLabel>
              <FieldValue>{physician.address || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_suite")}</FieldLabel>
              <FieldValue>{physician.suiteNumber || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_facility")}</FieldLabel>
              <FieldValue>{physician.facility || "-"}</FieldValue>
            </Field>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_city")}</FieldLabel>
              <FieldValue>{physician.city || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_state")}</FieldLabel>
              <FieldValue>{physician.state || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_zip")}</FieldLabel>
              <FieldValue>{physician.zipCode || "-"}</FieldValue>
            </Field>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_contact")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_phone")}</FieldLabel>
              <FieldValue>{physician.phone ? formatPhoneNumber(physician.phone) : "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_fax")}</FieldLabel>
              <FieldValue>{physician.fax ? formatPhoneNumber(physician.fax) : "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_email")}</FieldLabel>
              <FieldValue>{physician.email || "-"}</FieldValue>
            </Field>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("physician_management.form.section_personal")}</SectionTitle>
        <Row gutter={[24, 0]}>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_state_license")}</FieldLabel>
              <FieldValue>{physician.stateLicense || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_ptan")}</FieldLabel>
              <FieldValue>{physician.ptan || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_dea")}</FieldLabel>
              <FieldValue>{physician.dea || "-"}</FieldValue>
            </Field>
          </Col>
          <Col span={6}>
            <Field>
              <FieldLabel>{t("physician_management.detail.label_tax_id")}</FieldLabel>
              <FieldValue>{physician.taxId || "-"}</FieldValue>
            </Field>
          </Col>
        </Row>
      </Section>
    </PhysicianDetailContainer>
  );
};

export { PhysicianDetail };
